import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceAdvancedStrategy,
  useUpdateServiceAdvancedStrategy,
  ADVANCED_TYPE_VULNERABILITY_SCANNING,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './VulnerabilityScanningForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const TYPE = ADVANCED_TYPE_VULNERABILITY_SCANNING;

/** Action policies. Only "Block" is confirmed from a captured payload. */
const ACTIONS = [
  { value: 'Block', label: 'Block' },
  { value: 'Redirect', label: 'Redirect' },
  { value: 'By-pass', label: 'By-pass' },
] as const;

/** Request filter (request_type). "Injection Requests" is confirmed; the "All
 *  Requests" value is best-effort and may need adjusting against the portal. */
const REQUEST_FILTERS = [
  { value: 'vulnerability_attack_request', label: 'Injection Requests' },
  { value: 'all_request', label: 'All Requests' },
] as const;

/** Metrics strategy (dimension_key). Only "IP" is confirmed. */
const METRICS = [{ value: 'IP', label: 'IP' }] as const;

/** Vendor defaults for the fields the portal sends but doesn't expose in the form. */
const HIDDEN_DEFAULTS = { max_request_count: 500, min_sample_count: 2, request_dispersion: 0 };

/** The Vulnerability Scanning (type 7) per-type config. */
interface VulnConfig {
  request_type: string;
  dimension_key: string;
  limit_seconds: number;
  limit_times: number;
  action: string;
  block_value: number;
  delay: number;
  auto_black_ip: number;
  redirect_value: string;
  // Sent by the portal but not exposed in the form — preserved as-is.
  max_request_count: number;
  min_sample_count: number;
  request_dispersion: number;
}

interface FormState {
  name: string;
  description: string;
  sites: number[];
  request_type: string;
  dimension_key: string;
  limit_times: number;
  limit_seconds: number;
  action: string;
  block_value: number;
  delay: number;
  auto_black_ip: number;
  redirect_value: string;
  hidden: { max_request_count: number; min_sample_count: number; request_dispersion: number };
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  sites: [],
  request_type: 'vulnerability_attack_request',
  dimension_key: 'IP',
  limit_times: 20,
  limit_seconds: 60,
  action: 'Block',
  block_value: 418,
  delay: 0,
  auto_black_ip: 0,
  redirect_value: '',
  hidden: { ...HIDDEN_DEFAULTS },
});

/**
 * Add / Edit a Vulnerability Scanning rule (AI-WAF → Advanced Protection,
 * waf_advanced_strategy type 7). Builds the per-type `config`; the BE adds the
 * common envelope keys. Routes: aiwaf/advanced/vulnerability-scanning/new + /:id.
 */
export function VulnerabilityScanningForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;


  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<VulnConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = existing.config ?? ({} as VulnConfig);
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      sites: existing.sites ?? [],
      request_type: c.request_type || 'vulnerability_attack_request',
      dimension_key: c.dimension_key || 'IP',
      limit_times: c.limit_times ?? 20,
      limit_seconds: c.limit_seconds ?? 60,
      action: c.action || 'Block',
      block_value: c.block_value ?? 418,
      delay: c.delay ?? 0,
      auto_black_ip: c.auto_black_ip ?? 0,
      redirect_value: c.redirect_value ?? '',
      hidden: {
        max_request_count: c.max_request_count ?? HIDDEN_DEFAULTS.max_request_count,
        min_sample_count: c.min_sample_count ?? HIDDEN_DEFAULTS.min_sample_count,
        request_dispersion: c.request_dispersion ?? HIDDEN_DEFAULTS.request_dispersion,
      },
    });
  }, [existing]);

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceAdvancedStrategy<VulnConfig>(serviceId ?? '', TYPE, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'vulnerability_scanning_rule', 'create');
      show({ tone: 'success', title: 'Vulnerability Scanning rule created' });
      navigate(`${base}/aiwaf/advanced?tab=vulnerability-scanning`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceAdvancedStrategy<VulnConfig>(serviceId ?? '', TYPE, id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'vulnerability_scanning_rule', 'edit');
      show({ tone: 'success', title: 'Vulnerability Scanning rule saved' });
      navigate(`${base}/aiwaf/advanced?tab=vulnerability-scanning`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const actionValid = form.action !== 'Block' ? true : form.block_value > 0;
  const canSubmit = nameValid && form.limit_times > 0 && form.limit_seconds > 0 && actionValid && !saving;

  const submit = () => {
    const payload: AdvancedStrategy<VulnConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config: {
        request_type: form.request_type,
        dimension_key: form.dimension_key,
        limit_seconds: form.limit_seconds,
        limit_times: form.limit_times,
        action: form.action,
        block_value: form.block_value,
        delay: form.delay,
        auto_black_ip: form.auto_black_ip,
        redirect_value: form.action === 'Redirect' ? form.redirect_value.trim() : '',
        ...form.hidden,
      },
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: isEdit ? 'Edit Vulnerability Scanning Rule' : 'Vulnerability Scanning Rule' },
            ]}
          />
        }
        title="Vulnerability Scanning Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>General</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Triggering Condition</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Request Filter"
                required
                description="All Requests may be intercepted by the WAF first; Injection Requests sets bonded websites' WAF (if enabled) to Monitor mode."
              >
                {() => (
                  <Select value={form.request_type} onValueChange={(v) => set('request_type', v)}>
                    <SelectTrigger className={styles.selectLg}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REQUEST_FILTERS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field
                label="Metrics"
                required
                description="Strategy used for calculating the metrics. IP means the calculation is based on the same IP address."
              >
                {() => (
                  <Select value={form.dimension_key} onValueChange={(v) => set('dimension_key', v)}>
                    <SelectTrigger className={styles.selectMd}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {METRICS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field
                label="Threshold"
                required
                description="Threshold of the rate limit. Default: 20 requests in 60 seconds."
              >
                {() => (
                  <div className={styles.rateRow}>
                    <Input
                      type="number"
                      min={1}
                      value={String(form.limit_times)}
                      className={styles.rateInput}
                      onChange={(e) => set('limit_times', Number(e.target.value))}
                    />
                    <span className={styles.muted}>number of requests in</span>
                    <Input
                      type="number"
                      min={1}
                      value={String(form.limit_seconds)}
                      className={styles.rateInput}
                      onChange={(e) => set('limit_seconds', Number(e.target.value))}
                    />
                    <span className={styles.muted}>second(s)</span>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Action</CardTitle>
              <CardDescription>Configure how requests that satisfy the triggering condition are handled.</CardDescription>
            </CardHeader>
            <CardBody>
            <div className={styles.actionGrid}>
              <Field label="Action" required>
                {() => (
                  <Select value={form.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <div className={styles.actionRow}>
                <Field label="Delay (second(s))" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={0}
                      value={String(form.delay)}
                      className={styles.numField}
                      onChange={(e) => set('delay', Number(e.target.value))}
                    />
                  )}
                </Field>
                {form.action === 'Block' && (
                  <Field label="Response Status Code" required>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={100}
                        max={599}
                        value={String(form.block_value)}
                        className={styles.numField}
                        onChange={(e) => set('block_value', Number(e.target.value))}
                      />
                    )}
                  </Field>
                )}
                {form.action === 'Redirect' && (
                  <Field label="Redirect Path" required>
                    {(props) => (
                      <Input
                        {...props}
                        value={form.redirect_value}
                        placeholder="/blocked"
                        className={styles.redirectInput}
                        onChange={(e) => set('redirect_value', e.target.value)}
                      />
                    )}
                  </Field>
                )}
              </div>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Auto-Web ACL</CardTitle>
              <CardDescription>If a client's IP meets the criteria it is added to the Web ACL for the configured duration; during that time its requests are handled by the action above.</CardDescription>
            </CardHeader>
            <CardBody>
            <Field label="Duration (second(s))" required>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={String(form.auto_black_ip)}
                  className={styles.durationInput}
                  onChange={(e) => set('auto_black_ip', Number(e.target.value))}
                />
              )}
            </Field>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Apply to</CardTitle>
              <CardDescription>Select the websites this rule protects.</CardDescription>
            </CardHeader>
            <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.siteLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=vulnerability-scanning`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}
